import type { RouterOutputs } from "@repo/api";
import { useEffect } from "react";
import { startOfDay, subDays } from "date-fns";
import { useShallow } from "zustand/react/shallow";

import OrderActionContextDialog from "~/components/cart/order-action-context-dialog";
import OrderSettingsDialog from "~/components/cart/order-settings-dialog";
import OrderSplitDialog from "~/components/cart/order-split-dialog";
import OrderTransferDialog from "~/components/cart/order-transfer-dialog";
import CheckoutDialog from "~/components/cash-register/checkout-dialog";
import DayOverviewDialog from "~/components/cash-register/day-overview-dialog";
import ReceiptDialog from "~/components/cash-register/receipt-dialog";
import OnlineOrderDialog from "~/components/order/online-order-dialog";
import { OrderContext } from "~/hooks/use-order";
import { api } from "~/lib/api";
import { useTempBookingStore } from "~/stores/use-booking-store";
import { useLoadingStore } from "~/stores/use-loading-store";

export type Order = RouterOutputs["order"]["getActiveOrders"][number];
export type OnlineOrder = RouterOutputs["order"]["getOnlineOrders"][number];

export type OrderLine =
  RouterOutputs["order"]["getActiveOrders"][number]["items"][number];

export type PayTransaction = RouterOutputs["order"]["startTransaction"];

export type Payment = RouterOutputs["order"]["getPayments"][number];

interface OrderProviderProps {
  children: React.ReactNode;
}

export function OrderProvider({ children }: OrderProviderProps) {
  const { selectedDate } = useTempBookingStore(
    useShallow((state) => ({
      selectedDate: state.selectedDate,
    })),
  );
  const { data: orders, isFetching: isOrdersLoading } =
    api.order.getActiveOrders.useQuery(undefined, {
      initialData: [],
    });

  const { data: onlineOrders } = api.order.getOnlineOrders.useQuery(
    {
      startDate: startOfDay(subDays(selectedDate, 7)),
    },
    {
      initialData: [],
    },
  );

  const { setLoadingStage } = useLoadingStore(
    useShallow((state) => ({
      setLoadingStage: state.setCurrentLoadingStage,
    })),
  );

  const context = {
    orders,
    onlineOrders,
    isFetchingOrders: isOrdersLoading,
  };

  useEffect(() => {
    if (isOrdersLoading) {
      setLoadingStage("orders");
    } else {
      setLoadingStage(undefined);
    }
  }, [isOrdersLoading, setLoadingStage]);

  return (
    <OrderContext.Provider value={context}>
      <OrderActionContextDialog />
      <OrderTransferDialog />
      <OrderSplitDialog />
      <CheckoutDialog />
      <DayOverviewDialog />
      <ReceiptDialog />
      <OrderSettingsDialog />
      {children}
    </OrderContext.Provider>
  );
}
